import React, { useState } from 'react';
import { Loader2, Search, FolderInput, Trash2, Type } from 'lucide-react';
import { requestDriveRenameCheck, pollDriveJob } from './DriveRenamePrompt';

export const CleanupTab = ({ api, toast }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');

  const preview = async () => {
    setBusy('preview');
    try { setData((await api.get('/drive/cleanup/preview')).data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Preview failed'); }
    finally { setBusy(''); }
  };
  const apply = async () => {
    setBusy('apply');
    try {
      const { data: r } = await api.post('/drive/cleanup/apply');
      toast.message('Moving old folders into the new layout…');
      await pollDriveJob(api, r.job_id);
      toast.success('Old folders moved');
      preview();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Cleanup failed'); }
    finally { setBusy(''); }
  };
  const trash = async (id) => {
    try { await api.post('/drive/trash', { drive_id: id }); toast.success('Moved to Drive bin'); preview(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not remove'); }
  };

  if (data && data.connected === false) return <div className="text-sm text-slate-500" data-testid="cleanup-not-connected">Google Drive isn't connected.</div>;
  const fileCount = (data?.legacy || []).reduce((n, l) => n + l.files.length, 0);
  return (
    <div className="space-y-4" data-testid="drive-cleanup-tab">
      <div className="flex flex-wrap gap-2">
        <button data-testid="cleanup-preview-btn" onClick={preview} className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-md border border-[#E2E2DF] bg-white hover:bg-slate-50">
          {busy === 'preview' ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} Scan Drive
        </button>
        <button data-testid="cleanup-rename-all-btn" onClick={() => requestDriveRenameCheck({ all: true, force: true })} className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-md border border-[#E2E2DF] bg-white hover:bg-slate-50">
          <Type size={13} /> Rename everything to current names
        </button>
      </div>
      {data && (
        <>
          <Section title={`Old flat folders to move (${data.legacy.length} folders · ${fileCount} files)`} testId="cleanup-legacy">
            {data.legacy.map((l) => (
              <div key={l.drive_id} className="py-1.5">
                <div className="text-xs font-semibold text-slate-700">{l.name}</div>
                {l.files.map((f) => <div key={f.drive_id} className="text-[11px] font-mono text-slate-500 pl-4">{f.name} → {f.target}/</div>)}
              </div>
            ))}
            {data.legacy.length > 0 && (
              <button data-testid="cleanup-apply-btn" onClick={apply} disabled={!!busy} className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-md bg-[#0F4F1B] text-white hover:bg-[#0a3c14] disabled:opacity-50">
                {busy === 'apply' ? <Loader2 size={13} className="animate-spin" /> : <FolderInput size={13} />} Move {fileCount} files into the new layout
              </button>
            )}
          </Section>
          <Section title={`Folders with no matching job (${data.unmatched.length}) — left as they are`} testId="cleanup-unmatched">
            {data.unmatched.map((u) => <div key={u.drive_id} className="text-[11px] font-mono text-slate-500">{u.name} · {u.file_count} files</div>)}
          </Section>
          <Section title={`Duplicate files (${data.duplicates.length})`} testId="cleanup-duplicates">
            {data.duplicates.map((d) => (
              <div key={d.path + d.name} className="text-[11px] py-1">
                <span className="font-mono">{d.path}/{d.name}</span>
                {d.drive_ids.slice(1).map((id) => <TrashBtn key={id} onClick={() => trash(id)} id={id} />)}
              </div>
            ))}
          </Section>
          <Section title={`Empty folders (${data.empty_folders.length})`} testId="cleanup-empty">
            {data.empty_folders.map((f) => <div key={f.drive_id} className="text-[11px] py-0.5"><span className="font-mono">{f.name}</span> <TrashBtn onClick={() => trash(f.drive_id)} id={f.drive_id} /></div>)}
          </Section>
        </>
      )}
    </div>
  );
};

const Section = ({ title, children, testId }) => (
  <div className="bg-white border border-[#E2E2DF] rounded-md p-4" data-testid={testId}>
    <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">{title}</div>
    {children}
  </div>
);
const TrashBtn = ({ onClick, id }) => (
  <button data-testid={`cleanup-trash-${id}`} onClick={onClick} className="ml-2 inline-flex items-center gap-0.5 text-red-600 hover:underline"><Trash2 size={11} /> remove</button>
);
